(function () {
  // Keeps a box's price consistent with whether MaxBox Membership is actually in the cart,
  // in both directions:
  //   1. Box added at member price (via Join & Save, which stamps a shared "Bundle ID"
  //      property on both lines) but Membership later removed -> the box is removed too,
  //      since it's no longer honestly priced.
  //   2. Box already in cart at non-member price, then Membership added separately
  //      afterward (e.g. from the membership page) -> the box is swapped to its
  //      member-priced variant automatically.
  // Runs on live AJAX cart updates, on every cart-page load (covers removals that reload
  // the page directly instead of going through Dawn's AJAX flow - seen in practice with
  // Appstle's own cart line controls), and as a last check before checkout is allowed
  // through.

  function findLine(items, predicate) {
    for (var i = 0; i < items.length; i++) {
      if (predicate(items[i])) return items[i];
    }
    return null;
  }

  function isMembershipItem(item) {
    var props = item.properties || {};
    if (props['Added via'] === 'Box Builder - Join & Save') return true;
    var handle = window.maxboxMembershipProductHandle;
    return Boolean(handle) && item.handle === handle;
  }

  function findOrphanedBoxKeys(items) {
    var activeBundleIds = {};
    items.forEach(function (item) {
      var props = item.properties || {};
      if (isMembershipItem(item) && props['Bundle ID']) activeBundleIds[props['Bundle ID']] = true;
    });

    var orphaned = [];
    items.forEach(function (item) {
      var props = item.properties || {};
      var bundleId = props['Bundle ID'];
      if (bundleId && !isMembershipItem(item) && !activeBundleIds[bundleId]) {
        orphaned.push(item.key);
      }
    });
    return orphaned;
  }

  function cartUrls() {
    return {
      change: (typeof routes !== 'undefined' && routes.cart_change_url) || '/cart/change.js',
      add: (typeof routes !== 'undefined' && routes.cart_add_url) || '/cart/add.js'
    };
  }

  function postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.json();
    });
  }

  function removeOrphanedBoxes(items) {
    var orphanedKeys = findOrphanedBoxKeys(items);
    if (!orphanedKeys.length) return Promise.resolve(false);

    console.log('[maxbox-cart-guard] removing orphaned boxes (membership no longer in cart):', orphanedKeys);
    var urls = cartUrls();

    return orphanedKeys
      .reduce(function (chain, key) {
        return chain.then(function () {
          return postJson(urls.change, { id: key, quantity: 0 }).then(function (data) {
            console.log('[maxbox-cart-guard] removed', key, data);
          });
        });
      }, Promise.resolve())
      .then(function () {
        return true;
      })
      .catch(function (err) {
        console.error('[maxbox-cart-guard] failed to remove orphaned box', err);
        return false;
      });
  }

  function upgradeBoxesToMemberPricing(items) {
    var membershipHandle = window.maxboxMembershipProductHandle;
    var matchText = window.maxboxMemberVariantMatch || 'Member';
    if (!membershipHandle) return Promise.resolve(false);

    var membershipItem = findLine(items, isMembershipItem);
    if (!membershipItem) return Promise.resolve(false);

    var candidates = items.filter(function (item) {
      var props = item.properties || {};
      var alreadyMemberPriced = (item.variant_title || '').indexOf(matchText) !== -1;
      return Boolean(props['Box Type']) && !alreadyMemberPriced;
    });
    if (!candidates.length) return Promise.resolve(false);

    console.log('[maxbox-cart-guard] upgrading boxes to member pricing:', candidates.map(function (i) { return i.key; }));
    var urls = cartUrls();
    var upgradedAny = false;

    // One shared Bundle ID for this whole pass - reuse the membership line's existing one
    // if it already has one (e.g. a box was upgraded earlier), otherwise mint a single new
    // one and use it for every candidate box AND the membership line. Generating a fresh id
    // per box here was a real bug: with 2+ un-upgraded boxes in cart, each iteration
    // overwrote the membership line's Bundle ID with its own, so only the LAST box
    // processed ended up correctly paired - the earlier ones' Bundle ID no longer matched
    // anything active, and the very next reconcile pass would delete them outright as
    // "orphaned", not just mis-upgrade them.
    var sharedBundleId = (membershipItem.properties && membershipItem.properties['Bundle ID']) ||
      (Date.now().toString(36) + Math.random().toString(36).slice(2, 8));

    return candidates
      .reduce(function (chain, boxItem) {
        return chain.then(function () {
          return fetch('/products/' + boxItem.handle + '.js', { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .then(function (product) {
              var memberVariant = findLine((product.variants || []), function (variant) {
                return variant.title.indexOf(matchText) !== -1;
              });
              if (!memberVariant || memberVariant.id === boxItem.variant_id) return;

              upgradedAny = true;
              var boxProperties = Object.assign({}, boxItem.properties, { 'Bundle ID': sharedBundleId });

              var addPayload = { id: memberVariant.id, quantity: boxItem.quantity, properties: boxProperties };
              if (boxItem.selling_plan_allocation) {
                addPayload.selling_plan = boxItem.selling_plan_allocation.selling_plan.id;
              }

              // Remove the non-member line, add the member-priced variant back with the same
              // selections/quantity/subscription plan. The membership line's own Bundle ID
              // is stamped once after all boxes are processed, not per box.
              return postJson(urls.change, { id: boxItem.key, quantity: 0 })
                .then(function () {
                  return postJson(urls.add, { items: [addPayload] });
                });
            });
        });
      }, Promise.resolve())
      .then(function () {
        if (!upgradedAny) return false;
        var membershipProperties = Object.assign({}, membershipItem.properties, {
          'Added via': (membershipItem.properties && membershipItem.properties['Added via']) || 'Box Builder - Join & Save',
          'Bundle ID': sharedBundleId
        });
        return postJson(urls.change, { id: membershipItem.key, properties: membershipProperties }).then(function () {
          return true;
        });
      })
      .catch(function (err) {
        console.error('[maxbox-cart-guard] failed to upgrade box to member pricing', err);
        return false;
      });
  }

  // Runs both checks against one cart snapshot and reloads once if anything changed -
  // reload comes last so a change made by one check is reflected before the other runs.
  function reconcile(items, andThen) {
    removeOrphanedBoxes(items).then(function (removed) {
      if (removed) {
        if (andThen) andThen(true);
        else window.location.reload();
        return;
      }
      upgradeBoxesToMemberPricing(items).then(function (upgraded) {
        if (upgraded) {
          if (andThen) andThen(true);
          else window.location.reload();
        } else if (andThen) {
          andThen(false);
        }
      });
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  function init() {
    // The cart-drawer snippet and the cart page section can both end up including this
    // file on the same page load - guard so this only wires up once.
    if (window.__maxboxCartGuardInit) return;
    window.__maxboxCartGuardInit = true;

    // Covers same-page AJAX cart changes (drawer, quantity stepper) via Dawn's own pub/sub.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var cartData = event && event.cartData;
        if (!cartData || !cartData.items) return;
        reconcile(cartData.items);
      });
    }

    // Also covers changes that reload the page directly instead of going through Dawn's
    // AJAX flow (seen in practice: Appstle's subscription/membership scripts render their
    // own remove control on cart line items, submitting a plain form POST that bypasses
    // the cartUpdate event entirely). Checking the live cart on every load catches that too.
    fetchCart()
      .then(function (cart) {
        if (cart && cart.items) reconcile(cart.items);
      })
      .catch(function (err) {
        console.error('[maxbox-cart-guard] failed to read cart on load', err);
      });

    // Last line of defense: the checks above are async, so a customer acting right before
    // Checkout could reach it before cleanup finishes. Intercepts the checkout submit,
    // re-checks the live cart first, and only lets it through once confirmed consistent.
    document.addEventListener(
      'submit',
      function (event) {
        var submitter = event.submitter;
        if (!submitter || submitter.name !== 'checkout') return;

        var form = event.target;

        // Already verified this exact submission - let it through for real this time.
        // (requestSubmit() below re-dispatches 'submit', so without this the check would
        // otherwise run forever.)
        if (form.__maxboxCheckoutVerified) {
          form.__maxboxCheckoutVerified = false;
          return;
        }

        event.preventDefault();

        var proceed = function () {
          form.__maxboxCheckoutVerified = true;
          // form.submit() would skip the submit event entirely AND drop the clicked
          // button's name/value ("checkout") from the submitted data, which Shopify's
          // cart form needs to know to redirect to checkout rather than just reprocessing
          // the cart - requestSubmit(submitter) preserves both.
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter);
          } else {
            form.submit();
          }
        };

        fetchCart()
          .then(function (cart) {
            if (!cart || !cart.items) {
              proceed();
              return;
            }
            reconcile(cart.items, function (changed) {
              if (changed) {
                window.location.reload();
              } else {
                proceed();
              }
            });
          })
          .catch(function (err) {
            // Fail open - never block a real checkout over a script error.
            console.error('[maxbox-cart-guard] checkout guard failed, letting checkout through', err);
            proceed();
          });
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
